import React, {FC, useCallback, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import DataTable, {TableAction, TableColumn} from '../../../../shared/components/DataTable'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {SearchFilterBar} from '../shared/SearchFilterBar'
import {createContact, deleteContact, IContactModel, IContactRequest, searchContacts, updateContact} from './Api'

const emptyForm: IContactRequest = {name: '', email: '', phone: '', title: '', note: ''}

const validation = Yup.object().shape({
  name: Yup.string().trim().required('Ad soyad zorunludur').max(150),
  email: Yup.string().trim().required('E-posta zorunludur').email('Geçerli bir e-posta adresi giriniz').max(150),
  phone: Yup.string().trim().max(30),
  title: Yup.string().trim().max(150),
  note: Yup.string().max(500),
})

/** Kişi rehberi: mülakat katılımcıları buradan seçilir */
const ContactPage: FC = () => {
  const [query, setQuery] = useState('')
  const [contacts, setContacts] = useState<IContactModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<IContactModel | null>(null)
  const [deleting, setDeleting] = useState<IContactModel | null>(null)

  const fetchContacts = useCallback(async (page: number, q: string) => {
    setLoading(true)
    try {
      const payload = (await searchContacts(q, page)).data.payload
      setContacts(payload.content)
      setTotalPages(payload.totalPages)
      setCurrentPage(page)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Rehber yüklenemedi'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchContacts(0, query)
  }, [query, fetchContacts])

  const formik = useFormik<IContactRequest>({
    initialValues: emptyForm,
    validationSchema: validation,
    onSubmit: async (values, {setSubmitting}) => {
      try {
        if (editing) {
          await updateContact(editing.id, values)
          showSuccessToast('Kişi güncellendi')
        } else {
          await createContact(values)
          showSuccessToast('Kişi rehbere eklendi')
        }
        setFormOpen(false)
        await fetchContacts(currentPage, query)
      } catch (error) {
        showErrorToast(apiErrorMessage(error, 'Kaydedilemedi'))
      } finally {
        setSubmitting(false)
      }
    },
  })

  const openForm = (contact: IContactModel | null) => {
    setEditing(contact)
    formik.resetForm({
      values: contact
        ? {
            name: contact.name,
            email: contact.email,
            phone: contact.phone ?? '',
            title: contact.title ?? '',
            note: contact.note ?? '',
          }
        : emptyForm,
    })
    setFormOpen(true)
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await deleteContact(deleting.id)
      showSuccessToast('Kişi silindi')
      await fetchContacts(currentPage, query)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Silinemedi'))
    } finally {
      setDeleting(null)
    }
  }

  const fieldError = (field: keyof IContactRequest) =>
    formik.touched[field] && formik.errors[field] ? formik.errors[field] : undefined

  const input = (field: keyof IContactRequest, label: string, required = false, placeholder = '') => (
    <div className='col-md-6'>
      <label className={`form-label fw-bold ${required ? 'required' : ''}`}>{label}</label>
      <input
        type='text'
        className={`form-control ${fieldError(field) ? 'is-invalid' : ''}`}
        placeholder={placeholder}
        {...formik.getFieldProps(field)}
      />
      {fieldError(field) && <div className='invalid-feedback'>{fieldError(field)}</div>}
    </div>
  )

  const columns: TableColumn<IContactModel>[] = [
    {
      key: 'name',
      label: 'Kişi',
      render: (_, contact) => (
        <div className='d-flex flex-column'>
          <span className='fw-bolder text-gray-800'>{contact.name}</span>
          {contact.title && <span className='text-muted fs-7'>{contact.title}</span>}
        </div>
      ),
    },
    {key: 'email', label: 'E-posta'},
    {key: 'phone', label: 'Telefon', render: (value) => value || '-'},
  ]

  const actions: TableAction<IContactModel>[] = [
    {label: 'Düzenle', icon: 'bi bi-pencil', className: 'btn-light-primary me-2', onClick: (contact) => openForm(contact)},
    {label: 'Sil', icon: 'bi bi-trash', className: 'btn-light-danger', onClick: setDeleting},
  ]

  return (
    <>
      <PageTitle breadcrumbs={[]}>Kişi Rehberi</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Kişi Rehberi</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Mülakat katılımcıları; mülakatta yazılan yeni e-postalar rehbere otomatik eklenir
            </span>
          </h3>
          <div className='card-toolbar'>
            <button type='button' className='btn btn-sm btn-primary' onClick={() => openForm(null)}>
              <i className='bi bi-plus-lg'></i>
              Yeni Kişi
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <SearchFilterBar placeholder='Ad, e-posta, telefon veya unvan ile ara' onSearch={setQuery} onFilter={() => undefined} />
          <DataTable
            data={contacts}
            columns={columns}
            actions={actions}
            showPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => page >= 0 && page < totalPages && fetchContacts(page, query)}
            loading={loading}
            emptyMessage='Rehberde kişi yok'
          />
        </div>
      </div>

      <Modal show={formOpen} onHide={() => setFormOpen(false)} size='lg' dialogClassName='modal-dialog-centered' backdrop='static'>
        <form onSubmit={formik.handleSubmit} noValidate>
          <div className='modal-header'>
            <h5 className='modal-title'>{editing ? 'Kişiyi Düzenle' : 'Yeni Kişi'}</h5>
            <button type='button' className='btn-close' onClick={() => setFormOpen(false)} />
          </div>
          <div className='modal-body'>
            <div className='row g-5'>
              {input('name', 'Ad Soyad', true)}
              {input('email', 'E-posta', true, 'ornek@firma.com')}
              {input('phone', 'Telefon')}
              {input('title', 'Unvan / Departman', false, 'Örn: Yazılım Müdürü')}
              <div className='col-12'>
                <label className='form-label fw-bold'>Not</label>
                <input type='text' maxLength={500} className='form-control' {...formik.getFieldProps('note')} />
              </div>
            </div>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light' onClick={() => setFormOpen(false)}>
              İptal
            </button>
            <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting}>
              {formik.isSubmitting ? <span className='spinner-border spinner-border-sm' /> : editing ? 'Güncelle' : 'Kaydet'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        show={!!deleting}
        header='Silme Onayı'
        text={`"${deleting?.name ?? ''}" rehberden silinecek. Devam edilsin mi?`}
        handleClose={() => setDeleting(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {ContactPage}
